import dayjs from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';
import { cloneData, cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { IEventOpenAccessProtocol, OpenAccessProtocol, OpenAccessProtocolType } from './open-access-protocol.js';
import { Cardinal } from '../../core/cardinal.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';
import { VersionRange } from '../../core/version-range.js';

export class FlexiblePassProtocol extends OpenAccessProtocol implements IEventOpenAccessProtocol {
  static readonly TYPE_ID: string = '9b15f593b7f344df9bcd02997c22c482';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly type: OpenAccessProtocolType = OpenAccessProtocolType.FlexiblePass;

  private _uses: number | null;

  private constructor(
    public readonly eventValidityRule: PassEventValidityRule,

    // Keyed by event ID. List of strings represents compatible pass variations.
    public readonly eventConfiguration: Map<string, BooleanOrListOfString>,
    public readonly startDate: dayjs.Dayjs,
    public readonly expirationDate: dayjs.Dayjs,
    public readonly maxUses: Cardinal,
    uses: number | null,
  ) {
    super();
    this._uses = uses;
  }

  get uses(): number | null {
    return this._uses;
  }

  get $typeId(): string {
    return FlexiblePassProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(FlexiblePassProtocol.SCHEMA_VERSION);
  }

  private static create(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    maxUses: Cardinal,
    uses: number | null,
  }): FlexiblePassProtocol {
    return new FlexiblePassProtocol(
        args.eventValidityRule,
        args.eventConfiguration,
        args.startDate,
        args.expirationDate,
        args.maxUses,
        args.uses,
    );
  }

  static forTemplate(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
  }): FlexiblePassProtocol {
    return this.create({
      ...args,
      maxUses: Cardinal.finite(0),
      uses: null,
    });
  }

  static forInstance(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    maxUses: Cardinal,
  }): FlexiblePassProtocol {
    return this.create({
      ...args,
      uses: null,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): FlexiblePassProtocol {
    return this.create({
      eventValidityRule: toEnumFromString(MapUtils.getString(map, 'eventValidityRule'), PassEventValidityRule),
      eventConfiguration: MapUtils.getMap<string, BooleanOrListOfString>(
          map,
          'eventConfiguration',
          (key, value) => ({ key: key, value: BooleanOrListOfString.decode(value) })),
      startDate: context.decodeDate(MapUtils.getInteger(map, 'startDate')),
      expirationDate: context.decodeDate(MapUtils.getInteger(map, 'expirationDate')),
      maxUses: Cardinal.decode(MapUtils.getInteger(map, 'maxUses')),
      uses: MapUtils.getIntegerOrNull(map, 'uses'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'eventValidityRule': fromEnumAsString(this.eventValidityRule),
      'eventConfiguration': mapToData(
          this.eventConfiguration,
          (key, value) => ({ key: key, value: value.encode() }),
      ),
      'startDate': context.encodeDate(this.startDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'maxUses': this.maxUses.encode(),
      'uses': this.uses,
    };
  }

  clone(): this {
    return FlexiblePassProtocol.create({
      eventValidityRule: this.eventValidityRule,
      eventConfiguration: cloneMap(this.eventConfiguration, (v) => v.clone()),
      startDate: TimeZone.cloneDate(this.startDate),
      expirationDate: TimeZone.cloneDate(this.expirationDate),
      maxUses: this.maxUses.clone(),
      uses: this.uses,
    }) as this;
  }

  isSame(other: FlexiblePassProtocol): boolean {
    if(other === this) {
      return true;
    }

    return other.eventValidityRule === this.eventValidityRule &&
            isSameMap(other.eventConfiguration, this.eventConfiguration, isSameData) &&
            other.startDate.isSame(this.startDate) &&
            other.expirationDate.isSame(this.expirationDate) &&
            isSameData(other.maxUses, this.maxUses) &&
            other.uses === this.uses;
  }

  applyTemplateChanges(other: this): this {
    return FlexiblePassProtocol.create({
      eventValidityRule: this.eventValidityRule,
      eventConfiguration: cloneMap(other.eventConfiguration, (v) => v.clone()),
      startDate: TimeZone.cloneDate(this.startDate),
      expirationDate: TimeZone.cloneDate(this.expirationDate),
      maxUses: this.maxUses.clone(),
      uses: this.uses,
    }) as this;
  }

  cloneEventConfiguration(): Map<string, BooleanOrListOfString> {
    return cloneMap(this.eventConfiguration, (v) => v.clone());
  }

  isEventAssignedToPass({ eventId, passVariationId }: {
    eventId: string,
    passVariationId: string
  }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    const isInEventConfiguration = entry != null && (
      (entry.isList && entry.listValue.includes(passVariationId)) ||
      (entry.isBoolean && entry.booleanValue)
    );

    return this.eventValidityRule === PassEventValidityRule.IncludedEvents ? isInEventConfiguration : !isInEventConfiguration;
  }

  isEventAssignedToTemplate({ eventId }: { eventId: string }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    if(this.eventValidityRule === PassEventValidityRule.IncludedEvents) {
      return entry != null && (
        (entry.isList && entry.listValue.length > 0) ||
        (entry.isBoolean && entry.booleanValue)
      );
    }

    return entry == null || entry.isList || (entry.isBoolean && !entry.booleanValue);
  }

  getAssignedEventIdsForPass({ passVariationId }: { passVariationId: string }): string[] {
    const assignedEventIds: string[] = [];
    for(const eventId of this.eventConfiguration.keys()) {
      if(this.isEventAssignedToPass({ eventId, passVariationId })) {
        assignedEventIds.push(eventId);
      }
    }

    return assignedEventIds;
  }

  copyWith({
    eventValidityRule = this.eventValidityRule,
    eventConfiguration = cloneMap(this.eventConfiguration, (v) => v.clone()),
    maxUses = cloneData(this.maxUses),
    startDate = TimeZone.cloneDate(this.startDate),
    expirationDate = TimeZone.cloneDate(this.expirationDate),
    uses = this.uses,
  }: {
    eventValidityRule?: PassEventValidityRule,
    eventConfiguration?: Map<string, BooleanOrListOfString>,
    maxUses?: Cardinal,
    startDate?: dayjs.Dayjs,
    expirationDate?: dayjs.Dayjs,
    uses?: number | null,
  } = {}): this {
    return FlexiblePassProtocol.create({
      eventValidityRule,
      eventConfiguration,
      startDate,
      expirationDate,
      maxUses,
      uses,
    }) as this;
  }

  updateUsage(): void {
    if(this.maxUses.isInfinite || (this._uses ?? 0) < this.maxUses.value) {
      this._uses = (this._uses ?? 0) + 1;
    }
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof FlexiblePassProtocol;
  }

  static assertType(dm: DataModel): dm is FlexiblePassProtocol {
    return dm instanceof FlexiblePassProtocol;
  }
}

export class FlexiblePassProtocolBuilder extends DataModelBuilder<FlexiblePassProtocol> {
  build(context: SerializationContext, map: SerializedData): FlexiblePassProtocol {
    return FlexiblePassProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(FlexiblePassProtocol.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(FlexiblePassProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `maxUses` of type `Cardinal` and `uses` of type `int | null`
    if(version < 2) {
      // It's safe to deserialize this directly(instead of the migration safe decode) because the constructors
      // didn't allow null/undefined values for this field.
      const maxEventCount = Cardinal.decode(MapUtils.getInteger(map, 'maxEventCount'));
      map['maxUses'] = maxEventCount.encode();
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: FlexiblePassProtocol,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to;

    if(version >= 2) {
      version = 1;

      map['maxEventCount'] = model.maxUses.encode();
      map['$schema_version'] = VersionRange.encodedBetween(1, 2);
    }

    return map;
  }
}
